import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CheckboxGroupFormField from '../../../components/form/CheckboxGroupFormField';
import c from '../../../constant';
import useStore from '../../../store';
import { mappingKhmerNumber } from '../../../utils/market-development';
import EwsSummaryCompanyFormField from './ews-summary-company-field';
import EwsSummaryCompanyTypeFormField from './ews-summary-company-type-field';

const validationSchema = Yup.lazy((values) =>
	Yup.object({
		years: Yup.array().of(Yup.number().required('required')).nullable(),
		companyType: Yup.string().oneOf(['1', '2', '3', '4', '5'], 'invalid').nullable(),
		...(false === values?.industrial
			? {
					companyId: Yup.string().nullable(),
				}
			: {}),
	}),
);

export default function EwsAnnualSummaryFilter({
	industrial = false,
	reportId = null,
	report = null,
	companyType = null,
	companyId = null,
	years = null,
	onCallback = () => null,
}) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();

	return (
		<>
			<Button
				isLoading={false}
				isDisabled={false}
				onClick={onOpen}
				leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
				colorScheme={companyType || companyId || years ? 'yellow' : 'gray'}
				variant="solid"
				size="sm"
				borderRadius={c.borderRadius}
				border="1px solid #bdc3c7"
			>
				<Text fontSize="sm" fontWeight="semibold">
					{t('Filter')}
				</Text>
			</Button>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							validationSchema={validationSchema}
							initialValues={{
								industrial,
								companyType: companyType ?? '',
								companyId: companyId ?? '',
								years: years ? JSON.parse(years) : [],
							}}
							onSubmit={(values) => {
								let url = '?period=annually&reportId=' + reportId + '&report=' + report;
								if (values?.companyType) {
									url = url + `&companyType=${values?.companyType}`;
								}
								if (values?.companyId) {
									url = url + `&companyId=${values?.companyId}`;
								}
								if (values?.years?.length > 0) {
									url = url + `&years=${JSON.stringify(values?.years)}`;
								}
								onCallback(url);
								onClose();
							}}
						>
							{({ values, ...props }) => {
								return (
									<Box pb="4">
										<Form>
											<SimpleGrid columns={1} spacing={4}>
												<EwsSummaryCompanyTypeFormField
													{...props}
													name="companyType"
													label="Company Type"
													formNameCompanyId="companyId"
													placeholder={t('Company Type')}
													industrial={industrial}
												/>
												{false === values?.industrial && (
													<EwsSummaryCompanyFormField
														{...props}
														name="companyId"
														label="Company"
														formNameCompanyType="companyType"
														industrial={industrial}
													/>
												)}
												<CheckboxGroupFormField
													{...props}
													name="years"
													label="Years"
													columns={3}
													options={[0].reduce((p, c) => {
														let lp = p;
														let i = 2021;
														while (i <= moment().year()) {
															lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : `${i}`, value: `${i}` });
															i = i + 1;
														}
														return lp?.reverse();
													}, [])}
												/>
												<Flex mt={2} justifyContent="flex-end">
													<Button
														mr={2}
														colorScheme="gray"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														borderRadius={c.borderRadius}
														onClick={() => {
															let url = '?period=annually&reportId=' + reportId + '&report=' + report;
															onCallback(url);
															onClose();
														}}
													>
														{t('Clear')}
													</Button>
													<Button
														ml={2}
														colorScheme="blue"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														type="submit"
														borderRadius={c.borderRadius}
													>
														{t('Apply')}
													</Button>
												</Flex>
											</SimpleGrid>
										</Form>
									</Box>
								);
							}}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}
